/**
 * LDS package orders (lds_orders / FlightsHotel): single business-status model (SSOT).
 * UI labels / badge tones must use helpers here — not raw paid / bank_transfer / opStatus / reservation.
 * Keep aligned with: HotelsRiver_BE-main_whist/services/lds_order_business_status.py
 * Agent-facing copy (J5 / pre-auth, etc.): HotelsRiver_BE-main_whist/services/lds_order_agent_display.py
 * When API sends agent_display_message, prefer it — do not duplicate payment-flow rules here.
 */

import { resolveLdsFinancialState } from "./orders/resolveLdsFinancialState.js";
import {
  MIXED_PAYMENT_DISPLAY_KEYS,
  resolveMixedPaymentDisplayState,
} from "./orders/resolveMixedPaymentDisplayState.js";

/** @typedef {keyof typeof LDS_ORDER_BUSINESS_STATUS} LdsOrderBusinessStatusKey */

/**
 * Canonical business lifecycle (API + FE).
 * opStatus / SUCCESS never imply PAID or BOOKED alone; bank_transfer+!paid always WAITING_FOR_PAYMENT.
 */
export const LDS_ORDER_BUSINESS_STATUS = Object.freeze({
  WAITING_FOR_PAYMENT: "WAITING_FOR_PAYMENT",
  PARTIAL_PAYMENT: "PARTIAL_PAYMENT",
  PAID: "PAID",
  BOOKED: "BOOKED",
  PROPOSAL: "PROPOSAL",
  FAILED: "FAILED",
});

const _ALL_STATUS_VALUES = Object.values(LDS_ORDER_BUSINESS_STATUS);

/** Unified Hebrew copy: list/modal (full) vs profile sidebar (compact). */
export const LDS_ORDER_STATUS_COPY = Object.freeze({
  [LDS_ORDER_BUSINESS_STATUS.WAITING_FOR_PAYMENT]: {
    full: "ממתין להעברה בנקאית",
    compact: "ממתין להעברה",
  },
  [LDS_ORDER_BUSINESS_STATUS.PARTIAL_PAYMENT]: {
    full: "יתרה לתשלום",
    compact: "יתרה לתשלום",
  },
  [LDS_ORDER_BUSINESS_STATUS.PAID]: { full: "שולמה", compact: "שולם" },
  [LDS_ORDER_BUSINESS_STATUS.BOOKED]: { full: "מאושרת", compact: "מאושר" },
  [LDS_ORDER_BUSINESS_STATUS.PROPOSAL]: {
    full: "ממתינה לתשלום",
    compact: "הצעה בתהליך הזמנה",
  },
  [LDS_ORDER_BUSINESS_STATUS.FAILED]: {
    full: "נדרש בירור",
    compact: "חסר מידע",
  },
});

export function isKnownBusinessStatus(value) {
  return typeof value === "string" && _ALL_STATUS_VALUES.includes(value);
}

/** Backend-computed Hebrew line for agents (see lds_order_agent_display.attach_agent_display_inplace). */
export function getLdsOrderAgentDisplayMessage(order) {
  if (!order || typeof order !== "object") return null;
  const m = order.agent_display_message;
  if (typeof m !== "string") return null;
  const t = m.trim();
  return t.length ? t : null;
}

/** Backend-computed J5/payment progress steps (lds_order_agent_display.attach_agent_timeline_inplace). */
export function getLdsOrderAgentDisplayTimeline(order) {
  if (!order || typeof order !== "object") return null;
  const t = order.agent_display_timeline;
  if (!t || typeof t !== "object") return null;
  if (t.cancelled) return null;
  if (!Array.isArray(t.steps) || !t.steps.length) return null;
  return t;
}

/** Fully settled financially — never trust order.paid alone (split payments). */
export function isPaidOrder(order) {
  if (!order || typeof order !== "object") return false;
  return resolveLdsFinancialState(order).isFullyPaid;
}

export function isPartialPaidOrder(order) {
  if (!order || typeof order !== "object") return false;
  return resolveLdsFinancialState(order).isPartialPaid;
}

export function isBankTransferAwaitingPayment(order) {
  if (!order || typeof order !== "object") return false;
  const bt =
    order.bank_transfer === true ||
    order.bank_transfer === "true" ||
    order.bank_transfer === 1 ||
    order.later === true ||
    order.later === "true" ||
    order.later === 1;
  return bt && !isPaidOrder(order);
}

function _reservationBody(order) {
  const r = order?.order_response?.reservation;
  if (r == null || typeof r !== "object" || Array.isArray(r)) return null;
  return r;
}

/**
 * True only when reservation carries a real itinerary / PNR signal (not an empty {}).
 * Ambiguity: other LDS shapes may exist — we only inspect common keys; unknown → false.
 */
export function isReservationPresent(order) {
  const res = _reservationBody(order);
  if (!res || Object.keys(res).length === 0) return false;
  const pnr = res.supplierPNR ?? res.bookingRef;
  if (pnr != null && String(pnr).trim() !== "") return true;
  const h = res.hotel;
  if (h && typeof h === "object") {
    if (h.hotelName || h.hotelId || h.hotelid || h.checkInDate || h.checkOutDate) {
      return true;
    }
  }
  const t = res.tour;
  if (t && typeof t === "object" && (t.tourName || t.tourId)) return true;
  const fl = res.flight ?? res.flights;
  if (Array.isArray(fl) && fl.length > 0) return true;
  if (fl && typeof fl === "object" && !Array.isArray(fl) && Object.keys(fl).length > 0) {
    return true;
  }
  return false;
}

/**
 * order_response exists and is not an empty shell (empty {} or only empty nested stubs).
 */
export function isMeaningfulOrderResponse(order) {
  const resp = order?.order_response;
  if (resp == null) return false;
  if (typeof resp !== "object" || Array.isArray(resp)) return false;
  if (Object.keys(resp).length === 0) return false;
  const keys = Object.keys(resp);
  if (
    Object.prototype.hasOwnProperty.call(resp, "reservation") &&
    !isReservationPresent(order)
  ) {
    const besidesRes = keys.filter((k) => k !== "reservation");
    if (besidesRes.length === 0) return false;
  }
  if (isReservationPresent(order)) return true;
  const os = resp.opStatus;
  if (os && typeof os === "object") {
    const st = os.status;
    if (st != null && String(st).trim() !== "") return true;
  }
  if (resp.status != null && String(resp.status).trim() !== "") return true;
  if (resp.deal != null) return true;
  if (resp.dealId != null && String(resp.dealId).trim() !== "") return true;
  return Object.keys(resp).length > 0;
}

/**
 * @param {object|null|undefined} order
 * @param {{ trustApiBusinessStatus?: boolean }} [opts]
 * @returns {string} one of LDS_ORDER_BUSINESS_STATUS
 */
export function deriveLdsOrderBusinessStatus(order, opts = {}) {
  const trustApi = opts.trustApiBusinessStatus !== false;
  if (
    trustApi &&
    order &&
    typeof order === "object" &&
    isKnownBusinessStatus(order.business_status)
  ) {
    return order.business_status;
  }

  if (!order || typeof order !== "object") {
    return LDS_ORDER_BUSINESS_STATUS.FAILED;
  }

  if (isBankTransferAwaitingPayment(order)) {
    return LDS_ORDER_BUSINESS_STATUS.WAITING_FOR_PAYMENT;
  }

  const completionState = String(order.completion_state ?? "").trim();
  if (completionState === "INTERNAL_MANUAL_CONFIRMED") {
    return LDS_ORDER_BUSINESS_STATUS.BOOKED;
  }

  if (isReservationPresent(order)) {
    return LDS_ORDER_BUSINESS_STATUS.BOOKED;
  }

  const financial = resolveLdsFinancialState(order);
  if (financial.isFullyPaid) {
    return LDS_ORDER_BUSINESS_STATUS.PAID;
  }
  if (financial.isPartialPaid) {
    return LDS_ORDER_BUSINESS_STATUS.PARTIAL_PAYMENT;
  }

  if (isMeaningfulOrderResponse(order)) {
    return LDS_ORDER_BUSINESS_STATUS.PROPOSAL;
  }

  return LDS_ORDER_BUSINESS_STATUS.FAILED;
}

export function getLdsOrderBusinessStatusLabelHe(status, variant = "full") {
  const row = LDS_ORDER_STATUS_COPY[status];
  if (!row) return "—";
  return variant === "compact" ? row.compact : row.full;
}

/** Supplier queue flag (DB `pending`) — orthogonal to business_status; only for display priority. */
export function isLdsSupplierPendingRow(order) {
  if (!order || typeof order !== "object") return false;
  return order.pending === true || order.pending === "true" || order.pending === 1;
}

/**
 * One badge line for lists/cards/modals (cancelled + supplier pending first, then derive).
 */
export function getLdsOrderPrimaryBadgeText(order) {
  if (!order || typeof order !== "object") return "—";
  const agentLine = getLdsOrderAgentDisplayMessage(order);
  if (agentLine) return agentLine;
  if (order.cancelled) return "בוטלה";
  if (isLdsSupplierPendingRow(order)) return "ממתינה לאישור ספק";
  const mixed = resolveMixedPaymentDisplayState(order);
  if (mixed.applies && mixed.displayStatus) {
    return mixed.displayStatus;
  }
  const st = deriveLdsOrderBusinessStatus(order);
  return getLdsOrderBusinessStatusLabelHe(st, "full");
}

/**
 * Dashboard / modal: header accent key, summary strip key, badge text.
 * headerKey / summaryKey drive CSS groups in OrderDetailsHeader & OrderDetailsSummaryStrip.
 */
export function getLdsOrderDashboardPresentation(order) {
  if (!order || typeof order !== "object") {
    return {
      headerKey: "failed",
      summaryKey: "failed",
      badgeText: "—",
      businessStatus: LDS_ORDER_BUSINESS_STATUS.FAILED,
    };
  }
  const agentLine = getLdsOrderAgentDisplayMessage(order);
  if (order.cancelled) {
    return {
      headerKey: "cancelled",
      summaryKey: "cancelled",
      badgeText: agentLine ?? "בוטלה",
      businessStatus: deriveLdsOrderBusinessStatus(order),
    };
  }
  if (isLdsSupplierPendingRow(order)) {
    return {
      headerKey: "pending",
      summaryKey: "pending",
      badgeText: agentLine ?? "ממתינה לאישור ספק",
      businessStatus: deriveLdsOrderBusinessStatus(order),
    };
  }
  const st = deriveLdsOrderBusinessStatus(order);
  const mixed = resolveMixedPaymentDisplayState(order);
  const badgeText =
    agentLine ??
    (mixed.applies && mixed.displayStatus
      ? mixed.displayStatus
      : getLdsOrderBusinessStatusLabelHe(st, "full"));
  const headerKeyMap = {
    [LDS_ORDER_BUSINESS_STATUS.WAITING_FOR_PAYMENT]: "bank",
    [LDS_ORDER_BUSINESS_STATUS.PARTIAL_PAYMENT]: "proposal",
    [LDS_ORDER_BUSINESS_STATUS.PAID]: "paid",
    [LDS_ORDER_BUSINESS_STATUS.BOOKED]: "booked",
    [LDS_ORDER_BUSINESS_STATUS.PROPOSAL]: "proposal",
    [LDS_ORDER_BUSINESS_STATUS.FAILED]: "failed",
  };
  const summaryKeyMap = {
    [LDS_ORDER_BUSINESS_STATUS.WAITING_FOR_PAYMENT]: "waiting_payment",
    [LDS_ORDER_BUSINESS_STATUS.PARTIAL_PAYMENT]: "partial_payment",
    [LDS_ORDER_BUSINESS_STATUS.PAID]: "paid",
    [LDS_ORDER_BUSINESS_STATUS.BOOKED]: "booked",
    [LDS_ORDER_BUSINESS_STATUS.PROPOSAL]: "proposal",
    [LDS_ORDER_BUSINESS_STATUS.FAILED]: "failed",
  };
  const mixedHeaderKey =
    mixed.displayStatusKey === MIXED_PAYMENT_DISPLAY_KEYS.MIXED_PARTIAL_BANK_TRANSFER
      ? "mixed_payment"
      : null;
  return {
    headerKey: mixedHeaderKey || headerKeyMap[st] || "failed",
    summaryKey:
      mixedHeaderKey ||
      summaryKeyMap[st] ||
      "failed",
    badgeText,
    badges: mixed.applies ? mixed.badges : null,
    mixedPayment: mixed.applies ? mixed : null,
    businessStatus: st,
  };
}

/**
 * Business logic: order reached a terminal "settled" state (paid or supplier booking on record).
 * Use this (or deriveLdsOrderBusinessStatus) for branching — not only for CSS.
 */
export function isLdsOrderBusinessSettled(order) {
  if (!order || typeof order !== "object") return false;
  const st = deriveLdsOrderBusinessStatus(order);
  return (
    st === LDS_ORDER_BUSINESS_STATUS.PAID ||
    st === LDS_ORDER_BUSINESS_STATUS.BOOKED
  );
}

/** Heuristic: row shape from LDS/package APIs (not classic Gimmonix hotel-only orders). */
export function isLikelyLdsOrderRow(order) {
  if (!order || typeof order !== "object") return false;
  return (
    order.order_response != null ||
    order.bank_transfer === true ||
    order.bank_transfer === "true" ||
    order.bank_transfer === 1 ||
    order.dealid != null ||
    order.entity_type != null
  );
}

/**
 * Package/tour profile wizard: advance to summary stage when the row is no longer a pure draft.
 * Uses derive + explicit supplier flag; opStatus.PENDING alone is LDS async (supplier systems), kept only as technical signal here — not mixed with customer payment state without isLdsOrderBusinessSettled / isLdsSupplierPendingRow.
 */
export function ldsOrderWizardShouldShowSummaryStage(order) {
  if (!order || typeof order !== "object") return false;
  if (isLdsOrderBusinessSettled(order)) return true;
  if (isLdsSupplierPendingRow(order)) return true;
  const os = order.order_response?.opStatus?.status;
  return os === "PENDING";
}

/**
 * Table row CSS: success tone only for PAID / BOOKED business states.
 * Implementation = settled check; prefer isLdsOrderBusinessSettled in non-style code.
 */
export function ldsOrderRowIsSuccessTone(order) {
  return isLdsOrderBusinessSettled(order);
}

/**
 * SSOT: LDS row visible in agent/admin order tables (mirrors BE lds_order_feed_eligibility).
 * Uses DB paid flag — not financial settlement — so partial-paid ACTIVE rows stay visible.
 */
export function shouldIncludeLdsOrderInOrdersFeed(order) {
  if (!order || typeof order !== "object") return false;
  if (order.cancelled) return false;

  const paidFlag =
    order.paid === true || order.paid === "true" || order.paid === 1;
  if (paidFlag) return true;

  const bankTransfer =
    order.bank_transfer === true ||
    order.bank_transfer === "true" ||
    order.bank_transfer === 1;
  if (bankTransfer) return true;

  const bookingRef = String(order.booking_ref ?? "").trim();
  if (bookingRef) return true;

  const ors =
    order.order_response && typeof order.order_response === "object"
      ? order.order_response
      : {};
  if (
    String(ors.reservationSupplierStatus ?? "").trim().toUpperCase() === "ACTIVE"
  ) {
    return true;
  }

  const res =
    ors.reservation && typeof ors.reservation === "object" ? ors.reservation : {};
  const sabre = res.sabrePNR ?? res.sabrePnr ?? res.sabre_pnr;
  if (String(sabre ?? "").trim()) return true;

  const ppc = String(order.post_payment_checkout_status ?? "").trim();
  if (ppc === "SUPPLIER_CONFIRMED") return true;

  if (
    order.requires_admin_attention === true ||
    order.requires_admin_attention === "true"
  ) {
    return true;
  }

  return false;
}

/**
 * Legacy list visibility: inverse of feed SSOT (kept for hidden-stats breakdown).
 */
export function isHiddenBySupplierPendingGate(order) {
  if (!order || typeof order !== "object") return true;
  return !shouldIncludeLdsOrderInOrdersFeed(order);
}

export function passesOrdersDashboardPackagesGate(order) {
  return shouldIncludeLdsOrderInOrdersFeed(order);
}

export function isLdsInquiryReviewOrder(order) {
  if (!order || typeof order !== "object") return false;
  if (order.agent_display_status === "NEEDS_REVIEW") return true;
  if (order.business_status === LDS_ORDER_BUSINESS_STATUS.FAILED) return true;
  const message = String(order.agent_display_message ?? "").trim();
  if (message === "נדרש בירור") return true;
  return deriveLdsOrderBusinessStatus(order) === LDS_ORDER_BUSINESS_STATUS.FAILED;
}

export function getLdsOrderProfileSidebarBadge(order) {
  if (!order || typeof order !== "object") {
    return { label: LDS_ORDER_STATUS_COPY[LDS_ORDER_BUSINESS_STATUS.FAILED].compact, className: "is-pending" };
  }
  const agentLine = getLdsOrderAgentDisplayMessage(order);
  if (order.cancelled) {
    return { label: agentLine ?? "בוטלה", className: "is-pending" };
  }
  if (isLdsSupplierPendingRow(order)) {
    return { label: agentLine ?? "ממתינה לאישור ספק", className: "is-pending" };
  }
  const st = deriveLdsOrderBusinessStatus(order);
  const mixed = resolveMixedPaymentDisplayState(order);
  if (mixed.applies && mixed.displayStatus) {
    return {
      label: mixed.displayStatus,
      className: "is-mixed-payment",
      badges: mixed.badges,
    };
  }
  const label = agentLine ?? getLdsOrderBusinessStatusLabelHe(st, "compact");
  const className =
    st === LDS_ORDER_BUSINESS_STATUS.PAID
      ? "is-paid"
      : st === LDS_ORDER_BUSINESS_STATUS.BOOKED
        ? "is-confirmed"
        : st === LDS_ORDER_BUSINESS_STATUS.WAITING_FOR_PAYMENT
          ? "is-pending"
          : st === LDS_ORDER_BUSINESS_STATUS.FAILED
            ? "is-pending"
            : "is-draft";
  return { label, className };
}

/** Package list card — same primary text as modal badge. */
export function getLdsOrderPackageCardStatusText(row) {
  return getLdsOrderPrimaryBadgeText(row);
}

export {
  hasPendingBankTransfer,
  resolveMixedPaymentDisplayState,
} from "./orders/resolveMixedPaymentDisplayState.js";

export function isLdsProposalEntity(order) {
  return order?.entity_type === "proposal";
}

export function isLdsOrderBookingConfirmed(order) {
  if (!order || typeof order !== "object") return false;
  if (isLdsOrderResponseFailedForNavigation(order)) return false;
  return isLdsOrderBusinessSettled(order);
}

/**
 * True only for supplier-backed confirmation: `/packages/profile/:id/success` is reserved for
 * real booking / reservation on record — not bank transfer pending, not proposals, not paid-without-book.
 */
export function isPackageProfileBookingSuccessRouteAllowed(order) {
  if (!order || typeof order !== "object") return false;
  if (isLdsProposalEntity(order)) return false;
  if (isBankTransferAwaitingPayment(order)) return false;
  if (!isLdsOrderBookingConfirmed(order)) return false;
  return isReservationPresent(order);
}

/**
 * `/packages/profile/:id/success` — same rule as {@link isPackageProfileBookingSuccessRouteAllowed}.
 * Bank transfer / proposal / payment-pending stay on `/packages/profile/:id`.
 */
export function isPackageOrderSuccessRouteEligible(order) {
  return isPackageProfileBookingSuccessRouteAllowed(order);
}

/** Canonical non–draft customer profile path (no `/success`). */
export function getPackageOrderProfilePath(orderId) {
  if (orderId == null || orderId === "") return "/packages/profile";
  return `/packages/profile/${orderId}`;
}

/** opStatus on persisted `order_response` after LDS proposal/book calls on create/update. */
export function getLdsOrderSavedOpStatus(orderRow) {
  if (!orderRow || typeof orderRow !== "object") return null;
  const resp = orderRow.order_response;
  if (!resp || typeof resp !== "object") return null;
  const os = resp.opStatus ?? resp.data?.opStatus;
  if (!os || typeof os !== "object") return null;
  return os;
}

/**
 * True when persisted LDS `order_response` body looks like a hard failure
 * (aligned with HotelsRiver_BE services/lds_flights_module_proposal_guard._lds_response_fail_like).
 * Does not inspect paid/booking — combine with {@link isReservationPresent} for navigation gating.
 */
export function ldsOrderResponseBodyFailLike(resp) {
  if (!resp || typeof resp !== "object" || Array.isArray(resp)) return false;
  const st = String(resp.status ?? "").trim().toUpperCase();
  if (st && ["FAIL", "ERROR", "FAILED", "REJECTED"].includes(st)) return true;
  const op = resp.opStatus;
  if (op && typeof op === "object") {
    const ost = String(op.status ?? "").trim().toUpperCase();
    if (
      [
        "FAIL",
        "ERROR",
        "FAILED",
        "REJECTED",
        "INVALID_PARAM",
        "INVALID_FORM",
      ].includes(ost)
    ) {
      return true;
    }
  }
  if (resp.faultstring || resp["SOAP-ENV:Fault"]) return true;
  return false;
}

/**
 * LDS rejected supplier booking (or save) — do not treat as checkout success while unpaid-like signals absent.
 * When a real reservation exists, failure flags on the envelope are ignored (supplier confirmed).
 */
export function isLdsOrderResponseFailedForNavigation(order) {
  if (!order || typeof order !== "object") return false;
  return (
    ldsOrderResponseBodyFailLike(order.order_response) &&
    !isReservationPresent(order)
  );
}

export function isLdsSavedOrderValidationFailed(orderRow) {
  const os = getLdsOrderSavedOpStatus(orderRow);
  const st = String(os?.status || "").toUpperCase();
  return st === "INVALID_PARAM" || st === "INVALID_FORM";
}

export function getLdsSavedOrderValidationMessage(orderRow) {
  if (!isLdsSavedOrderValidationFailed(orderRow)) return null;
  const os = getLdsOrderSavedOpStatus(orderRow);
  const m = os?.message ?? os?.Message;
  return typeof m === "string" && m.trim() ? m.trim() : null;
}

export function getPackageOrderSuccessPath(orderId) {
  if (orderId == null || orderId === "") return "/packages/profile";
  return `/packages/profile/${orderId}/success`;
}
